import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { useNavigate } from "react-router-dom";
import StatusOrderChip from "@/app/global-components/StatusOrderChip";
import { isUndefined } from "lodash";
import TableComponent from "@/app/global-components/table";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ROLE } from "@/types";
import { routes } from "@/consts";
import useOrder from "@/hooks/use-order";

interface Product {
  itemId: string;
  item: string;
  quantity: number;
  price: number;
}

interface Order {
  id: string;
  title: string;
  deliverDate: string;
  address: string;
  products: Product[];
  Sendedproducts: Product[];
  OrderNr: number;
  status: string;
  tender: { title: string };
  company: {
    nameAr: string;
    CompanyNr: string;
  };
}

const OrdersBList: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const navigate = useNavigate();
  const { fetchBuyerOrders } = useOrder();
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const [listOrders, setListOrders] = useState<Order[]>([]);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  // console.log(myCompany);
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (myCompany?.id) {
          const result = await fetchBuyerOrders(myCompany.id);
          setListOrders(result?.data || []);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
      }
    };
    fetchOrders();
  }, [myCompany?.id]);

  const calculateTotalPrice = (products: Product[]) =>
    products.reduce(
      (sum, product) => sum + product.price * product.quantity,
      0
    );

  const columns = [
    {
      title: t("orders.title"),
      field: "title",
      render: (row: Order) => row.title || "",
    },
    {
      title: t("company.fields.name"),
      field: "companyName",
      render: (row: Order) => row.company.CompanyNr,
      hideOnMobile: true,
    },
    {
      title: t("orders.fields.totalPrice"),
      field: "totalPrice",
      render: (row: Order) =>
        `${calculateTotalPrice(row.products).toFixed(2)} ${t(
          "checkout.summary.currency"
        )}`,
    },
    {
      title: t("orders.fields.deliveryDate"),
      field: "deliverDate",
      render: (row: Order) => new Date(row.deliverDate).toLocaleDateString(),
    },
    {
      title: t("orders.fields.status"),
      field: "status",
      render: (row: Order) => <StatusOrderChip status={row.status} />,
    },
  ];

  const handleRowClick = (row: Order) => {
    navigate(`${routes.order}/${row.id}`);
  };

  return (
    <div
      className="bg-white py-4 rounded-xl mx-4 md:px-10 relative"
      style={{ direction: "rtl" }}
    >
      <div className="text-2xl font-bold text-zinc-400 text-center">
        {t("orders.listTitle")}{" "}
        {userRole === ROLE.SELLER ? t("general.sales") : t("general.Purchases")}
      </div>
      <TableComponent
        columns={columns}
        data={listOrders}
        onRowClick={handleRowClick}
        rowKeyField="id"
        isMobile={window.innerWidth <= 767}
        maxHeight={300}
      />
    </div>
  );
};

export default OrdersBList;
